import React, { useState, useEffect, useCallback } from 'react';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { ReactSVG } from 'react-svg';
import clsx from 'clsx';
import { useSearchRoomContext } from '@/features/marketplace/hooks/use-search-room';
import { useHeaderVisibility } from '@/contexts/header-visibility-context';
import { useVirtualViewport } from '@/hooks/use-virtual-viewport';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/use-locale';
import { COLOC_MAIN_APP } from '@/config';
import { theme } from '@/utils/util_theme';
import { SearchBarHeader } from '@/features/marketplace/components/search-bar';
import { CitiesListContextProvider, useCitiesListContext } from '@/features/marketplace/hooks/use-get-cities';
import { CityListItem } from '@/features/marketplace/components/city-list-item';
import { getCityUrl } from '@/features/marketplace/utils/city-navigation';
import { LoadingSpinner2 } from '@/components/design-system/atoms/loading-spinner2';

const MobileSearchBar = () => {
  const [isSearching, setIsSearching] = useState(false);
  const { currentLocale } = useLocale();
  const viewportHeight = useVirtualViewport();
  const { setSearchQuery, searchQuery, debouncedSearchQuery, fetchSearchRoomResults } = useSearchRoomContext();
  const { isFetching, data: searchResults } = fetchSearchRoomResults;
  const { setHeaderVisible } = useHeaderVisibility();
  const { citiesList, fetchCitiesList } = useCitiesListContext();
  const { t } = useTranslation();

  useEffect(() => {
    setHeaderVisible(!isSearching);
    return () => {
      setHeaderVisible(true);
    };
  }, [isSearching, setHeaderVisible]);

  useEffect(() => {
    if (!isSearching) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSearching]);

  const handleClose = useCallback(() => {
    setSearchQuery(undefined);
    setIsSearching(false);
  }, [setSearchQuery]);


  const isPending = !!searchQuery && searchQuery !== debouncedSearchQuery;
  const isWaiting = isPending || isFetching;
  const firstResult = searchResults?.data?.[0];

  const handleSearch = () => {
    if (isWaiting) return;
    if (searchQuery && firstResult) {
      window.location.href = firstResult.path;
    } else {
      window.location.href = `${COLOC_MAIN_APP}/${currentLocale}/shared-houses`;
    }
  };

  return (
    <>
      {isSearching && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('js.components.mobile_search_bar.search_button')}
          className="fixed inset-x-0 top-0 z-50 flex flex-col bg-white"
          style={{ height: viewportHeight }}
        >
          <div className="flex-shrink-0 p-4 pb-0 text-start">
            <button
              type="button"
              onClick={handleClose}
              aria-label={t('js.components.mobile_search_bar.close_button', 'Close')}
              className="rounded-full bg-bg-primaryMedium p-2"
            >
              <ReactSVG
                src={require('@assets/icons/icon-sax/linear/close.svg')}
                beforeInjection={(svg) => {
                  svg.setAttribute('width', '24');
                  svg.setAttribute('height', '24');
                }}
              />
            </button>
          </div>

          <div className="flex-shrink-0 px-4 pt-3">
            <SearchBarHeader
              showDropdownOnEmptyFocus={false}
              showGotoSearchPageButton={false}

              className="mr-0 max-w-none min-w-0 w-full searchbar-mobile"
            />
          </div>

          <div className="flex-grow overflow-y-auto p-4 pt-0 mt-4">
            {!searchQuery && (
              <div>
                <div className="flex items-center justify-between pb-3">
                  <span className="text-medium font-medium text-text-primaryHeavy">
                    {t('js.components.mobile_search_bar.suggested_cities', 'Suggested Cities')}
                  </span>
                  <a
                    href={`${COLOC_MAIN_APP}/${currentLocale}/shared-houses`}
                    className="text-medium font-medium text-text-primaryHeavy"
                  >
                    {t('js.components.mobile_search_bar.show_all_houses', 'Show all houses')}
                  </a>
                </div>
                {fetchCitiesList.isLoading ? (
                  <LoadingSpinner2 />
                ) : citiesList.length > 0 ? (
                  citiesList.map((city) => (
                    <a key={city.id} href={getCityUrl(city, currentLocale)}>
                      <div className="border-b-[1px] border-solid border-E50 py-2">
                        <div className="flex w-full items-center">
                          <CityListItem
                            city={city}
                            imgSize={32}
                            imgClassName="mb-1 mr-[10px] rounded-[10px] min-w-[32px]"
                            nameClassName="text-[12px] font-bold leading-[1.4] text-text-secondary"
                            availableRoomsClassName="text-text-success"
                            textContainerClassName="relative flex w-full flex-col"
                          />
                        </div>
                      </div>
                    </a>
                  ))
                ) : null}
              </div>
            )}
          </div>

          <div className="flex flex-shrink-0 bg-white px-4 py-3">
            <button
              type="button"
              onClick={handleSearch}
              aria-busy={isWaiting}
              disabled={isWaiting}
              className={clsx(
                'flex flex-1 items-center justify-center',
                'h-[56px] rounded-[16px] px-3 py-[12px]',
                'bg-R300',
                'text-[16px] font-semibold text-text-primaryHeavy',
                isWaiting && 'cursor-wait opacity-70'
              )}
            >
              <IconSaxIcon
                name="search-normal-1"
                size={24}
                strokeColor={theme?.colors.black || '#000000'}
                strokeWidth={1.5}
                className="mr-2"
              />
              {t('js.components.mobile_search_bar.search_button')}
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={clsx(
          'flex items-center justify-center',
          'mt-4 h-[56px] w-full rounded-[16px] px-[20px] py-[12px]',
          'bg-R300',
          'text-[16px] font-medium text-text-primaryHeavy'
        )}
        onClick={() => setIsSearching(true)}
      >
        <IconSaxIcon
          name="search-normal-1"
          size={24}
          strokeColor={theme?.colors.black || '#000000'}
          strokeWidth={1.5}
          className="mr-2"
        />
        {t('js.components.mobile_search_bar.find_home_button')}
      </button>
    </>
  );
};

const MobileSearchBarWithCities = () => {
  return (
    <CitiesListContextProvider>
      <MobileSearchBar />
    </CitiesListContextProvider>
  );
};

export { MobileSearchBarWithCities as MobileSearchBar };
